/**
 * Squad & Tactics API Service (Microservicio de Plantillas y Tácticas)
 * Conecta con los endpoints del backend y en caso de que aún no existan o fallen,
 * simula la llamada cargando el mock (.json) desde /mocks/squad/
 */
import { BackendFormation, ClubLineupResponse, LineupSelectionResponse, LineupSlot, Player, SaveLineupPayload, SaveLineupSelectionPayload } from '../models/types';
import { apiSimulator } from './api-simulator.service';

export const squadApi = {
  /**
   * GET /api/squads/formations
   * Mock fallback: /mocks/squad/formations.json
   */
  async getFormations(): Promise<BackendFormation[]> {
    return apiSimulator.callWithMockFallback<BackendFormation[]>({
      method: 'GET',
      endpoint: '/api/squads/formations',
      service: 'squad',
      mockName: 'formations',
    });
  },

  /**
   * GET /api/clubs/{clubId}/lineup-selection
   * Consulta selección habitual de once y banquillo (0-5 suplentes).
   * Sin fallback a mock: propaga respuestas reales.
   */
  async getLineupSelection(clubId: string): Promise<LineupSelectionResponse> {
    return apiSimulator.callWithMockFallback<LineupSelectionResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/lineup-selection`,
      service: 'squad',
      allowMockFallback: false,
    });
  },

  /**
   * PUT /api/clubs/{clubId}/lineup-selection
   * Guarda formación, 11 titulares con sus slots y banquillo (hasta 5 suplentes).
   * Sin fallback a mock: propaga respuestas reales.
   */
  async saveLineupSelection(
    clubId: string,
    payload: SaveLineupSelectionPayload
  ): Promise<LineupSelectionResponse> {
    return apiSimulator.callWithMockFallback<LineupSelectionResponse>({
      method: 'PUT',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/lineup-selection`,
      body: payload,
      service: 'squad',
      allowMockFallback: false,
    });
  },

  /**
   * 1. GET /api/clubs/{clubId}/lineup
   * Consulta formación y once titulares actuales del club.
   * Mock fallback: /mocks/squad/get-lineup.json
   */
  async getClubLineup(clubId: string): Promise<ClubLineupResponse> {
    return apiSimulator.callWithMockFallback<ClubLineupResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/lineup`,
      service: 'squad',
      mockName: 'get-lineup',
    });
  },

  /**
   * 2. POST /api/clubs/{clubId}/lineup/auto
   * Devuelve una propuesta de alineación óptima (sin guardarla) para una formación específica.
   * Propaga los errores reales; no sustituye el once por datos simulados.
   */
  async autoPickLineup(
    clubId: string,
    formation: string
  ): Promise<ClubLineupResponse> {
    return apiSimulator.callWithMockFallback<ClubLineupResponse>({
      method: 'POST',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/lineup/auto`,
      body: { formation },
      service: 'squad',
      mockName: 'auto-pick',
      allowMockFallback: false,
      transformMock: (mock) => ({
        ...mock,
        formation,
      }),
    });
  },

  /**
   * 3. PUT /api/clubs/{clubId}/lineup
   * Guarda la formación y alineación de 11 jugadores del club.
   * Mock fallback: /mocks/squad/save-lineup.json
   */
  async saveClubLineup(
    clubId: string,
    payload: SaveLineupPayload
  ): Promise<ClubLineupResponse> {
    return apiSimulator.callWithMockFallback<ClubLineupResponse>({
      method: 'PUT',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/lineup`,
      body: payload,
      service: 'squad',
      mockName: 'save-lineup',
      transformMock: (mock) => ({
        ...mock,
        formation: payload.formation,
        version: (payload.expectedVersion ?? 0) + 1,
        players: payload.players.map((p) => ({
          slot: p.slot,
          playerInstanceId: p.playerInstanceId,
        })),
      }),
    });
  },
};
